import clsx from 'clsx';
import { FC, HTMLAttributes } from 'react';
import { FiMinusCircle, FiPlusCircle } from 'react-icons/fi';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { InputProps } from '../withField';

interface CounterInputProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange' | 'value'> {
  max?: number;
  min?: number;
  disabled?: boolean;
  allowManualChange?: boolean;
}

export const Counter: FC<InputProps<number> & CounterInputProps> = ({
  value = 0,
  min = 0,
  max,
  onChange,
  className,
  disabled,
  allowManualChange,
  ...props
}) => {
  const canIncrease = max === undefined || value < max;
  const canDecrease = value > min;

  const decrease = () => {
    if (canDecrease) {
      onChange(value - 1);
    }
  };

  const increase = () => {
    if (canIncrease) {
      onChange(value + 1);
    }
  };

  const onManualChange = (value: number) => {
    if (value >= min && (max === undefined || value < max)) {
      onChange(value);
    }
  };

  return (
    <div {...props} className={clsx('flex items-center gap-0.5', className)}>
      <Button
        variant={ButtonVariant.Ghost}
        disabled={disabled}
        className={canDecrease ? 'text-primary-700' : 'text-gray-300'}
        onClick={e => {
          e.stopPropagation();
          decrease();
        }}
      >
        <FiMinusCircle />
      </Button>
      {allowManualChange ? (
        <input
          onChange={event => {
            const number = parseInt(event.target.value.replace(/[^\d.]+/, ''), 10);
            if (isNaN(number)) {
              onManualChange(0);
            }

            onManualChange(number);
          }}
          value={value}
          className="text-center w-[30px] border-b"
        />
      ) : (
        <span className="min-w-[18px] text-center font-semibold">{value}</span>
      )}
      <Button
        variant={ButtonVariant.Ghost}
        disabled={disabled}
        className={canIncrease ? 'text-primary-700' : 'text-gray-300'}
        onClick={e => {
          e.stopPropagation();
          increase();
        }}
      >
        <FiPlusCircle />
      </Button>
    </div>
  );
};
